﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// A page for the last step in the password reset workflow. Displays an input field where the user can
// enter their new password.

// Can be tested in jRåd.
// Click on the "Forgot username or password" link at the login page.

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { TitleText } from "~base/Components/Primitives/TitleText";
import Grid from "~base/Components/Layout/Grid";
import { useIntl } from "~base/Internationalization/IntlContext";
import { Rubrik } from "~base/Components/Primitives/Rubrik";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import {
    getSpacingClasses,
    useJssSpacingStyles,
} from "~base/Components/Layout/JssSpacing";
import classnames from "classnames";
import { horizontalCenterPadding } from "~base/Components/Primitives/TitleContainer";
import { reducer } from "./Store";
import { useAccountPostResetPassword } from "~base/Services/API/Account/PostResetPassword";
import { useSearchParams } from "react-router-dom";
import { useSettings } from "~base/Settings/SettingsContext";
import Form from "~base/Components/Primitives/Form";
import ApplicationPage from "~base/Pages/Shared/ApplicationPage";
import { Button } from "~base/Components/Atoms/Button";
import TitlePassword from "~base/Components/Primitives/TitlePassword";
import { useNavigateService } from "~base/Services/NavigateService";
import { ApiStatus, getErrorMessage } from "~base/Services/API";
import TextBlock from "~base/Components/Primitives/TextBlock";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type SetPasswordProps = unknown;

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const SetPasswordPage: React.FunctionComponent<SetPasswordProps> = (
    props: SetPasswordProps
) => {
    const [searchParams] = useSearchParams();
    const username = searchParams.get("username");
    const token = searchParams.get("token");

    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("SetPassword"),
        reducer,
        { newPassword: "", errorMessage: null, isLoading: false }
    );

    const navigate = useNavigateService();

    const jssSpacingStyles = useJssSpacingStyles();

    const formVerticalSpacingClasses = getSpacingClasses(
        jssSpacingStyles,
        null,
        horizontalCenterPadding
    );

    const intl = useIntl();

    const settings = useSettings();
    const serverVirtualPath = settings.valueAsString("serverVirtualPath");
    const detectedTenant = settings.valueAsString("detectedTenantName");

    const [loadingSetPasswordApi, setPasswordInvokeApi] =
        useAccountPostResetPassword({
            callback: async (status: ApiStatus, response: Response) => {
                if (status == "Success") {
                    dispatch({ type: "setLoading" });

                    const tenantPart = detectedTenant
                        ? `tenantName=${encodeURIComponent(detectedTenant)}&`
                        : "";
                    const loginUrl = `${serverVirtualPath}Account/Login?${tenantPart}username=${encodeURIComponent(username)}`;
                    navigate.current(loginUrl);
                } else {
                    dispatch({
                        type: "setError",
                        errorMessage: await getErrorMessage(intl, response),
                    });
                }
            },
        });

    const loading = loadingSetPasswordApi || state.isLoading;

    const formIsValid = state.newPassword.length > 0;

    return (
        <ApplicationPage isLoading={loading}>
            <Form
                testIdentifier={"SetPasswordForm"}
                onSubmit={() => {
                    if (state.errorMessage)
                        dispatch({ type: "setError", errorMessage: null });
                    setPasswordInvokeApi({
                        newPassword: state.newPassword,
                        token: token,
                        username: username,
                        tenant: detectedTenant,
                    });
                }}
                isValid={formIsValid}
            >
                <Rubrik
                    textCode="VisiFinance.UI.SetPassword.Title"
                    rubrikSizeType="Medium"
                />

                <Grid>
                    <TitleText
                        testIdentifier="VisiFinance.UI.SetPassword.Username"
                        titleTextCode="VisiFinance.UI.SetPassword.Username"
                        value={username}
                        name={"Username"}
                        enabled={false}
                    />

                    <TitlePassword
                        testIdentifier="VisiFinance.UI.SetPassword.NewPassword"
                        titleTextCode="VisiFinance.UI.SetPassword.NewPassword"
                        value={state.newPassword}
                        name="Password"
                        autoFocus={true}
                        autoComplete={"new-passsword"}
                        isValid={(x) => x.length > 0}
                        setValue={(x) =>
                            dispatch({
                                type: "updateFormCredentials",
                                newPassword: x,
                            })
                        }
                    />

                    {state.errorMessage && (
                        <TextBlock
                            styleType="Paragraph"
                            size="Small"
                            styleSubType="Validation"
                            text={state.errorMessage}
                        />
                    )}

                    <Button
                        testIdentifier={"SetPasswordSubmitButton"}
                        type="submit"
                        disabled={!formIsValid}
                        className={classnames(formVerticalSpacingClasses)}
                    >
                        {intl.formatText("VisiFinance.UI.SetPassword.Ändra")}
                    </Button>
                </Grid>
            </Form>
        </ApplicationPage>
    );
};
